import React, { useCallback, useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { gpuVerdictAtom, gpuVerdictReasonAtom } from "../../atoms/gpuVerdictAtom";
import { resetGpuVerdict } from "../../utils/gpuVerdict";
import { logger } from "../../utils/logger";

/** Set when the reader clicks "Reload to try 3D", so a reload that still lands
 *  in stack-only can say so instead of repeating the first notice. Per-tab. */
export const RETRIED_KEY = "a2z.gpuVerdict.retried.v1";

function readRetried(): boolean {
  try {
    return sessionStorage.getItem(RETRIED_KEY) === "true";
  } catch (e) {
    logger.warn("[GpuVerdictBanner] sessionStorage read failed:", e);
    return false;
  }
}

/** Top banner shown for as long as the viewer is in stack-only mode. Not
 *  dismissible: a reader who can see they're in 2D can reload to get the 3D
 *  views back, and saves made in 2D are unaffected (ENG-265).
 *
 *  A denylisted GPU gets its own copy and no reload button, since every
 *  reload lands on the same verdict (ENG-266). Ops can still force 3D with
 *  ?force_stack=0. */
export const GpuVerdictBanner: React.FC = () => {
  const verdict = useAtomValue(gpuVerdictAtom);
  const reason = useAtomValue(gpuVerdictReasonAtom);
  const [retried] = useState(readRetried);
  const unsupportedGpu = reason === "denylist";

  // Recovery: clear the cached verdict + any override and reload so the probe
  // runs fresh. The escape hatch for a capable machine that got a stale/flaky
  // stack-only — the verdict is no longer a silent, frozen dead-end.
  const reloadToTry3D = useCallback(() => {
    try {
      sessionStorage.setItem(RETRIED_KEY, "true");
    } catch (e) {
      logger.warn("[GpuVerdictBanner] sessionStorage write failed:", e);
    }
    resetGpuVerdict();
    window.location.reload();
  }, []);

  // The retry worked — drop the marker so a later stack-only in this tab
  // starts from the first notice again.
  useEffect(() => {
    if (verdict !== "volume-ok") return;
    try {
      sessionStorage.removeItem(RETRIED_KEY);
    } catch (e) {
      logger.warn("[GpuVerdictBanner] sessionStorage remove failed:", e);
    }
  }, [verdict]);

  if (verdict !== "stack-only") return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full bg-surface-2 border-b border-border-strong px-4 py-2 text-sm text-fg flex items-center gap-3"
    >
      <div className="flex-1">
        <strong>2D slice viewing only.</strong>{" "}
        {unsupportedGpu
          ? "This computer's graphics hardware or settings can't run 3D views. 2D reading and saves work normally."
          : retried
            ? "3D views are still unavailable in this browser. You can keep reading in 2D — saves are not affected."
            : "3D views (e.g. multiplanar reconstruction for sag/cor) aren't available in this browser right now."}
      </div>
      {!unsupportedGpu && (
        <button
          type="button"
          onClick={reloadToTry3D}
          className="shrink-0 rounded border border-border-strong px-2 py-0.5 text-xs text-fg hover:bg-white/5"
        >
          Reload to try 3D
        </button>
      )}
    </div>
  );
};
